/**
 * Utility functions for managing meta tags dynamically
 */

/**
 * Sets or updates a meta tag in the document head
 */
function setMetaTag(attribute: string, value: string, content: string): void {
  let metaTag = document.querySelector<HTMLMetaElement>(
    `meta[${attribute}="${value}"]`
  );
  
  if (!metaTag) {
    metaTag = document.createElement('meta');
    if (attribute === 'name') {
      metaTag.name = value;
    } else if (attribute === 'property') {
      metaTag.setAttribute('property', value);
    }
    document.head.appendChild(metaTag);
  }
  
  metaTag.content = content;
}

/**
 * Sets the page title
 */
export function setPageTitle(title: string): void {
  document.title = title;
}

/**
 * Sets the meta description
 */
export function setMetaDescription(description: string): void {
  setMetaTag('name', 'description', description);
}

/**
 * Sets the author meta tag (used by crawlers e.g. LinkedIn).
 */
export function setAuthorMeta(author: string): void {
  setMetaTag('name', 'author', author);
}

/**
 * Sets og:article:published_time when present (optional; used by some platforms).
 */
export function setArticlePublishedTime(isoDate: string): void {
  setMetaTag('property', 'article:published_time', isoDate);
}

/**
 * Sets OpenGraph meta tags
 */
export function setOpenGraphTags(config: {
  title: string;
  description: string;
  type?: string;
  url?: string;
  image?: string;
}): void {
  setMetaTag('property', 'og:title', config.title);
  setMetaTag('property', 'og:description', config.description);
  setMetaTag('property', 'og:type', config.type || 'website');
  
  if (config.url) {
    setMetaTag('property', 'og:url', config.url);
  }
  
  if (config.image) {
    setMetaTag('property', 'og:image', config.image);
  }
}

/**
 * Sets Twitter Card meta tags
 */
export function setTwitterCardTags(config: {
  card?: string;
  title: string;
  description: string;
  image?: string;
}): void {
  setMetaTag('name', 'twitter:card', config.card || 'summary_large_image');
  setMetaTag('name', 'twitter:title', config.title);
  setMetaTag('name', 'twitter:description', config.description);
  
  if (config.image) {
    setMetaTag('name', 'twitter:image', config.image);
  }
}

/**
 * CMS-friendly: set all meta tags from a single config (e.g. from SEO overrides + page).
 * Falls back to defaults for any missing fields.
 */
export function setPageMeta(config: {
  title?: string | null;
  description?: string | null;
  keywords?: string | null;
  url?: string | null;
  image?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  twitterCard?: string | null;
  twitterTitle?: string | null;
  twitterDescription?: string | null;
  canonical?: string | null;
  noIndex?: boolean | null;
  schemaJson?: string | null;
}): void {
  const baseUrl = config?.url || window.location.origin;
  const image = config?.image || `${baseUrl}/social-share-gsgo.jpeg`;
  const title = config?.title || 'GSGO';
  const description = config?.description || 'Global Support for Growth and Opportunity.';
  const ogTitle = config?.ogTitle ?? title;
  const ogDesc = config?.ogDescription ?? description;
  const twTitle = config?.twitterTitle ?? title;
  const twDesc = config?.twitterDescription ?? description;

  setPageTitle(title);
  setMetaDescription(description);

  if (config?.keywords) {
    setMetaTag('name', 'keywords', config.keywords);
  }

  // robots: noindex when explicitly set
  if (config?.noIndex) {
    setMetaTag('name', 'robots', 'noindex, nofollow');
  } else {
    // Ensure robots tag is set to index when not suppressed
    const robotsMeta = document.querySelector<HTMLMetaElement>('meta[name="robots"]');
    if (robotsMeta && robotsMeta.content === 'noindex, nofollow') {
      robotsMeta.content = 'index, follow';
    }
  }

  setOpenGraphTags({
    title: ogTitle,
    description: ogDesc,
    type: 'website',
    url: baseUrl,
    image,
  });
  setTwitterCardTags({
    card: (config?.twitterCard as 'summary_large_image' | undefined) || 'summary_large_image',
    title: twTitle,
    description: twDesc,
    image,
  });

  if (config?.canonical) {
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'canonical';
      document.head.appendChild(link);
    }
    link.href = config.canonical;
  }

  // Structured data (JSON-LD)
  const existingScript = document.querySelector<HTMLScriptElement>('script[data-seo-schema]');
  if (config?.schemaJson) {
    if (existingScript) {
      existingScript.textContent = config.schemaJson;
    } else {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-seo-schema', 'true');
      script.textContent = config.schemaJson;
      document.head.appendChild(script);
    }
  } else if (existingScript) {
    existingScript.remove();
  }
}

/**
 * Sets all meta tags according to GSGO specifications (legacy / fallback).
 */
export function setGSGOMetaTags(config?: {
  title?: string;
  description?: string;
  url?: string;
  image?: string;
}): void {
  const baseUrl = config?.url || window.location.origin;
  const ogImage = config?.image || `${baseUrl}/social-share-gsgo.jpeg`;
  setPageMeta({
    title: config?.title || 'GSGO',
    description: config?.description || 'Global Support for Growth and Opportunity.',
    url: baseUrl,
    image: ogImage,
  });
}
